import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { PaymentElement, useElements, useStripe, Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions, StripeElementLocale } from '@stripe/stripe-js';
import { FormattedMessage, useIntl } from 'react-intl';
import type { PaymentFormRef, Transaction } from '@/features/wallet/types';
import { useStripePromise } from '@/shared/stripe';
import { useLocale } from '@/shared/localization/useLocale';
import { createStripePayment, createTransaction } from '@/features/wallet/api';
import { useTransactionStatusPolling } from '../../hooks';
import PaymentLoadingState from '../PaymentLoadingState';
import PaymentError from '../PaymentError';
import PaymentErrorBoundary from '../PaymentErrorBoundary';
import PaymentTimeoutError from '../PaymentTimeoutError';
import Spinner from '@/shared/components/Spinner';

export interface StripePaymentFormProps {
  amountCents: number;
  scheduleId?: string;
  onPaymentConfirmed: (transaction: Transaction) => void;
  onReady?: () => void;
  onError?: () => void;
  ref?: Ref<PaymentFormRef>;
}

type Status = 'loading' | 'ready' | 'submitting' | 'confirming' | 'error';

interface StripeIntent {
  clientSecret: string;
  customerSessionClientSecret: string | null;
}

interface InnerFormProps {
  onComplete: () => void;
  onReady?: () => void;
  onError: (message: string) => void;
  submitFnRef: React.RefObject<(() => Promise<void>) | null>;
}

function InnerStripeForm({ onComplete, onReady, onError, submitFnRef }: InnerFormProps) {
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();

  const doSubmit = useCallback(async () => {
    if (!stripe || !elements) return;

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (result.error) {
      const msg = result.error.message ?? intl.formatMessage({ id: 'wallet.payment.failed' });
      onError(msg);
      throw new Error(msg);
    }

    onComplete();
  }, [stripe, elements, intl, onComplete, onError]);

  useLayoutEffect(() => {
    submitFnRef.current = doSubmit;
  }, [doSubmit, submitFnRef]);

  return <PaymentElement options={{ terms: { card: 'never' } }} onReady={onReady} />;
}

export default function StripePaymentForm({ ref, amountCents, scheduleId, onPaymentConfirmed, onReady, onError }: StripePaymentFormProps) {
  const intl = useIntl();
  const stripePromise = useStripePromise();
  const locale = useLocale();

  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<ReactNode | null>(null);
  const [intent, setIntent] = useState<StripeIntent | null>(null);
  const [transactionId, setTransactionId] = useState<number | null>(null);
  const submitFnRef = useRef<(() => Promise<void>) | null>(null);

  const handleTimeout = useCallback(() => {
    setError(<PaymentTimeoutError />);
    setStatus('error');
    onError?.();
  }, [onError]);

  const { isPolling, startPolling } = useTransactionStatusPolling({
    transactionId,
    onConfirmed: onPaymentConfirmed,
    onTimeout: handleTimeout,
    onError: handleTimeout,
  });

  // Create payment intent on mount
  useEffect(() => {
    let cancelled = false;

    const createIntent = async () => {
      try {
        const transactionResponse = await createTransaction(amountCents, { scheduleId });

        if (cancelled) return;

        setTransactionId(transactionResponse.transaction_id);

        if (transactionResponse.gateway !== 'stripe') {
          throw new Error('Unexpected gateway type');
        }

        const stripeResponse = await createStripePayment(transactionResponse.transaction_id);
        setIntent({
          clientSecret: stripeResponse.client_secret,
          customerSessionClientSecret: stripeResponse.customer_session_client_secret,
        });
        setStatus('ready');
      } catch (error_) {
        if (cancelled) return;
        const message = error_ instanceof Error ? error_.message : intl.formatMessage({ id: 'wallet.topUp.error' });
        setError(message);
        setStatus('error');
      }
    };

    void createIntent();

    return () => {
      cancelled = true;
    };
  }, [amountCents, scheduleId, intl]);

  const handlePaymentComplete = useCallback(() => {
    setStatus('confirming');
    startPolling();
  }, [startPolling]);

  const handleError = useCallback((message: string) => {
    setError(message);
    setStatus('error');
  }, []);

  useImperativeHandle(ref, () => ({
    async submit() {
      setStatus('submitting');
      setError(null);
      try {
        if (submitFnRef.current) {
          await submitFnRef.current();
        }
      } catch (error_) {
        // Error already handled by onError callback
        setStatus('ready');
        throw error_;
      }
    },
  }), []);

  if (status === 'loading' && !intent) {
    return <PaymentLoadingState />;
  }

  if (status === 'error' && !intent) {
    return <PaymentError message={error} />;
  }

  if (!intent) {
    return <PaymentLoadingState />;
  }

  const showOverlay = status === 'submitting' || status === 'confirming' || isPolling;
  const elementsOptions: StripeElementsOptions = intent.customerSessionClientSecret
    ? { clientSecret: intent.clientSecret, customerSessionClientSecret: intent.customerSessionClientSecret, locale: locale as StripeElementLocale }
    : { clientSecret: intent.clientSecret, locale: locale as StripeElementLocale };

  return (
    <div className="relative space-y-4 p-4">
      <PaymentError message={error} />

      {showOverlay && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-lg">
          <div className="flex items-center">
            <Spinner />
            <span className="ml-2 text-sm text-slate-600">
              <FormattedMessage
                id={status === 'confirming' || isPolling ? 'wallet.payment.waitingConfirmation' : 'wallet.payment.processing'}
              />
            </span>
          </div>
        </div>
      )}

      <PaymentErrorBoundary>
        <Elements stripe={stripePromise} options={elementsOptions}>
          <InnerStripeForm
            onComplete={handlePaymentComplete}
            onReady={onReady}
            onError={handleError}
            submitFnRef={submitFnRef}
          />
        </Elements>
      </PaymentErrorBoundary>
    </div>
  );
}
